// ===== Packages =====

import React, {
    useMemo,
    useRef,
    useEffect,
}                                       from 'react';
import { ReactSVG }                     from 'react-svg';
import {
    useDrag,
    useDrop,
    DropTargetMonitor,
}                                       from 'react-dnd';

// ===== Components =====

import Tooltip                          from '../Tooltip';

// ===== Enums =====

import {
    DRAGGABLE_COMPONENT_TYPE,
    USER_ACTION_TYPE,
    TOOLTIP_TYPE,
}                                       from '../../enums';

// ===== Services =====

import {
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    recordUserAction,
    isTestMode,
    resolveOutlineHoverReorderIndex,
}                                       from '../../services';

// ===== Interfaces =====

import {
    IReadPostChapterItem,
    IReadPostSectionItem,
    ISnackbarItem,
    IPostPathEvent,
}                                       from '../../interfaces';

// ===== Constants =====

import {
    HOVER_TARGET_CLASSNAME,
    EDITOR_SECTION_INPUT_ID_PREFIX,
    EDITOR_SECTION_INPUT_CLASSNAME,
    OUTLINE_DRAG_INDICATOR_TRANSITION_DURATION,
    OUTLINE_DRAG_INDICATOR_X_OFFSET,
    DEFAULT_SNACKBAR_VISIBLE_DURATION,
    GRAB_DRAG_INDICATOR_DELAY_DURATION,
    SERIALIZED_TRUE_INITIAL_EDITOR_VALUE,
}                                       from '../../constants/generalConstants';
import KEYCODE                          from '../../constants/keycodes';

// ===== Images =====

import CautionIcon                      from '../../images/caution.svg';
import PencilIcon                       from '../../images/editor/pencil.svg';
import HighlightIcon                    from '../../images/editor/highlight.svg';
import DragIndicatorIcon                from '../../images/editor/drag-indicator.svg';
import DisabledIcon                     from '../../images/disabled.svg';

// ===== Styles =====

import {
    Container,
    OutlineChapterSectionInput,
    OutlineSectionDragIndicator,
    SectionNonEmptyIndicator,
    SectionAnnotationsIndicator,
    SectionDisabledIndicator,
}                                       from './styles';
import {
    OutlineNumberText,
}                                       from '../ReaderOutlineChapter/styles';

interface Props {
    customCursorActive: boolean,
    currentSessionId: string | null,
    postId: string,
    chapterIndex: number,
    numSections: number,
    section: IReadPostSectionItem,
    annotationCount: number,
    transitionDuration: number,
    chapters: IReadPostChapterItem[],
    sections: IReadPostSectionItem[],
    interactionDisabled: boolean,
    updateSectionTitle: (chapterId: string, sectionId: string, title: string) => void,
    flushSectionTitle: (chapterId: string, sectionId: string, title: string) => void,
    onDragIndicatorMouseEnter: (e: React.MouseEvent) => void,
    onDragIndicatorMouseLeave: (e: React.MouseEvent) => void,
    onDragIndicatorMouseDown: (e: React.MouseEvent | React.TouchEvent) => void,
    onDragIndicatorMouseUp: () => void,
    onDragIndicatorMouseMove: (type: USER_ACTION_TYPE) => void,
    onInputMouseEnter: (e: React.MouseEvent) => void,
    onInputMouseLeave: (e: React.MouseEvent) => void,
    onInputFocus: (e: React.FocusEvent) => void,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    selectedPostValuePath: number[],
    onChangeSelectedPostValuePath: (event: IPostPathEvent) => void,
    previewMoveSection: (chapterId: string, draggedSectionId: string, droppedIndex: number) => void,
    commitMoveSection: (
        chapterId: string,
        draggedSectionId: string,
        droppedIndex: number,
        originalIndex: number,
    ) => void,
    resetMoveSectionPreview: () => void,
    deleteSection: (chapterId: string, sectionIndex: number) => void,
}
function ReaderOutlineSection({
    customCursorActive,
    currentSessionId,
    postId,
    chapterIndex,
    numSections,
    section,
    annotationCount,
    transitionDuration,
    chapters,
    sections,
    interactionDisabled,
    updateSectionTitle,
    flushSectionTitle,
    onDragIndicatorMouseEnter,
    onDragIndicatorMouseLeave,
    onDragIndicatorMouseDown,
    onDragIndicatorMouseUp,
    onDragIndicatorMouseMove,
    onInputMouseEnter,
    onInputMouseLeave,
    onInputFocus,
    setSnackbarData,
    selectedPostValuePath,
    onChangeSelectedPostValuePath,
    previewMoveSection,
    commitMoveSection,
    resetMoveSectionPreview,
    deleteSection,
}: Props): JSX.Element {
    // ===== General Constants =====

    const SNACKBAR_MESSAGE_POST_VALUE_SAVING_ERROR = 'Section is saving. Please try again in a few seconds.';

    // ===== Refs =====

    const containerRef = useRef<HTMLDivElement | null>(null);
    const chapterIdRef = useRef<string>(chapters[chapterIndex].id);
    const sectionIdRef = useRef<string>(section.id);
    const sectionIndexRef = useRef<number>(section.index);

    // ===== State =====

    // Set up drag source for chapter section
    const [{ isDragging }, drag, preview] = useDrag(
        () => ({
            type: DRAGGABLE_COMPONENT_TYPE.editorOutlineSection,
            item: () => ({
                id: sectionIdRef.current,
                index: sectionIndexRef.current,
                originalIndex: sectionIndexRef.current,
                chapterId: chapterIdRef.current,
            }),
            canDrag: () => !interactionDisabled,
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
            end: (item, monitor) => {
                const didDrop = monitor.didDrop();

                if (!didDrop) {
                    resetMoveSectionPreview();
                    return;
                }

                if (item.index !== item.originalIndex) {
                    commitMoveSection(item.chapterId, item.id, item.index, item.originalIndex);
                }
            },
        }),
        [
            interactionDisabled,
            resetMoveSectionPreview,
            commitMoveSection,
        ],
    );

    // Set up drop target for chapter section
    const [, drop] = useDrop<{ id: string, index: number, originalIndex: number, chapterId: string }, void, unknown>(
        () => ({
            accept: DRAGGABLE_COMPONENT_TYPE.editorOutlineSection,
            hover(item, monitor: DropTargetMonitor<{ id: string, index: number, originalIndex: number, chapterId: string }, unknown>) {
                const hoveredSectionId = sectionIdRef.current;
                const hoveredSectionIndex = sectionIndexRef.current;
                const hoverBoundingRect = containerRef.current?.getBoundingClientRect();
                const clientOffset = monitor.getClientOffset();
                const nextPreviewIndex = resolveOutlineHoverReorderIndex({
                    interactionDisabled,
                    draggedId: item.id,
                    hoveredId: hoveredSectionId,
                    draggedIndex: item.index,
                    hoveredIndex: hoveredSectionIndex,
                    isShallowOver: monitor.isOver({ shallow: true }),
                    isSameContainer: item.chapterId === chapterIdRef.current,
                    hoverTop: hoverBoundingRect?.top,
                    hoverBottom: hoverBoundingRect?.bottom,
                    clientOffsetY: clientOffset?.y,
                });
                if (nextPreviewIndex == null) return;

                previewMoveSection(item.chapterId, item.id, nextPreviewIndex);
                // eslint-disable-next-line no-param-reassign
                item.index = nextPreviewIndex;
            },
        }),
        [
            interactionDisabled,
            previewMoveSection,
        ],
    );

    // ===== Animation Constants =====

    // Duration (in milliseconds) for non-empty chapter indicator to appear
    const CHAPTER_INDICATOR_TRANSITION_DURATION = GRAB_DRAG_INDICATOR_DELAY_DURATION;

    // ===== Methods =====

    /**
     * Manages determining whether keystroke is enter or escape
     * If enter: moves focus to section input
     * If escape: blurs focused section input
     * If backspace/delete: removes focused section input
     * @param e keyboard event
     */
    const checkForEnter = (e: React.KeyboardEvent): void => {
        if (e.key === KEYCODE.enter && !interactionDisabled) {
            // Move from outline chapter section to next chapter section if it exists
            if (document.activeElement?.classList.contains(EDITOR_SECTION_INPUT_CLASSNAME)) {
                // Prevents newline character from being placed in next chapter section
                e.preventDefault();

                const nextSectionChildren = document.activeElement?.parentElement?.nextSibling?.childNodes;
                if (nextSectionChildren) {
                    const nextChapterSection = Array
                        .from(nextSectionChildren)
                        .filter((node: ChildNode) => node.nodeName === 'INPUT')[0] as HTMLInputElement;
                    if (nextChapterSection) {
                        nextChapterSection.focus();
                    }
                }
            }
        } else if (e.key === KEYCODE.escape && !interactionDisabled) {
            // Blur Focused Input
            (document.activeElement as HTMLInputElement).blur();
        } else if (
            (
                e.key === KEYCODE.backspace
                || e.key === KEYCODE.delete
            )
            && !interactionDisabled
        ) {
            // Delete Outline Chapter Section
            // Backspaced on empty section
            if (
                document.activeElement?.classList.contains(EDITOR_SECTION_INPUT_CLASSNAME)
                && (document.activeElement as HTMLInputElement).value.length === 0
            ) {
                const sectionIdParts = document.activeElement.id.split('-');
                const deleteSectionIndex = parseInt(sectionIdParts[2], 10);
                onDeleteSection(deleteSectionIndex);
            }
        }
    };

    /**
     * Updates text of a chapter section in contents table
     * @param e input event
     */
    const onOutlineSectionInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        updateSectionTitle(chapters[chapterIndex].id, section.id, e.target.value);
    };

    const onOutlineSectionInputBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
        flushSectionTitle(chapters[chapterIndex].id, section.id, e.target.value);
    };

    /**
     * Removes a section from a chapter in the contents table
     * @param index index of the chapter to remove the section from
     * @param sectionIndex index of the section to remove
     */
    const onDeleteSection = (sectionIndex: number): void => {
        if (interactionDisabled) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_POST_VALUE_SAVING_ERROR,
                icon: CautionIcon,
                hasFailure: true,
            });
            return;
        }
        const removed = sections[sectionIndex];
        deleteSection(chapters[chapterIndex].id, sectionIndex);

        // Record User Action
        if (currentSessionId) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.deletePostChapterSection,
                sessionId: currentSessionId,
                payload: {
                    postId,
                    chapterId: chapters[chapterIndex].id,
                    sectionId: removed.id,
                },
            });
        }
    };

    /**
     * Manages modifying selected post value path to this section
     */
    const selectSection = (): void => {
        if (interactionDisabled) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_POST_VALUE_SAVING_ERROR,
                icon: CautionIcon,
                hasFailure: true,
            });
            return;
        }
        if (selectedPostValuePath[1] !== section.index) {
            onChangeSelectedPostValuePath({
                path: [chapterIndex, section.index],
            });
        }
    };

    // ===== Memoization =====

    const nonEmptyIndicatorVisible = useMemo(() => (
        !!section.value && section.value.length > SERIALIZED_TRUE_INITIAL_EDITOR_VALUE.length
    ), [
        section.value,
    ]);

    useEffect(() => {
        chapterIdRef.current = chapters[chapterIndex].id;
        sectionIdRef.current = section.id;
        sectionIndexRef.current = section.index;
    }, [
        chapters,
        chapterIndex,
        section.id,
        section.index,
    ]);

    const isSelected = useMemo(
        () => selectedPostValuePath.length === 2
            && selectedPostValuePath[0] === chapterIndex
            && selectedPostValuePath[1] === section.index,
        [
            selectedPostValuePath,
            chapterIndex,
            section,
        ],
    );

    // ===== Render =====

    return (
        <Container
            // Ensures that drag preview and drop target is entire chapter
            ref={(node) => {
                containerRef.current = node;
                if (node) {
                    drop(preview(node));
                }
            }}
            isDragging={isDragging}
            selected={isSelected}
            disabled={interactionDisabled}
            transitionDuration={transitionDuration}
            {...getPressEventHandlers({
                onTouchStart: !interactionDisabled ? selectSection : undefined,
                onMouseDown: !interactionDisabled ? selectSection : undefined,
            })}
        >
            <OutlineNumberText>
                {`${chapterIndex + 1}.${section.index + 1}`}
            </OutlineNumberText>
            <OutlineChapterSectionInput
                type="text"
                id={`${EDITOR_SECTION_INPUT_ID_PREFIX}-${chapterIndex}-${section.index}`}
                className={`${HOVER_TARGET_CLASSNAME} ${EDITOR_SECTION_INPUT_CLASSNAME}`}
                customCursorActive={customCursorActive}
                defaultValue={section.title}
                placeholder="Section Title"
                onKeyDown={checkForEnter}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: onInputMouseEnter,
                    onMouseLeave: onInputMouseLeave,
                })}
                onFocus={onInputFocus}
                onChange={(e) => onOutlineSectionInputChange(e)}
                onBlur={onOutlineSectionInputBlur}
            />
            {numSections > 1 && !interactionDisabled && (
                <OutlineSectionDragIndicator
                    ref={drag} // Isolates drag interaction to this element
                    className={HOVER_TARGET_CLASSNAME}
                    horizontalOffset={OUTLINE_DRAG_INDICATOR_X_OFFSET}
                    transitionDuration={OUTLINE_DRAG_INDICATOR_TRANSITION_DURATION}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: onDragIndicatorMouseEnter,
                        onMouseLeave: onDragIndicatorMouseLeave,
                        onTouchMove: () => (!interactionDisabled ? onDragIndicatorMouseMove(USER_ACTION_TYPE.grabOutlineSection) : undefined),
                        onMouseMove: () => (!interactionDisabled ? onDragIndicatorMouseMove(USER_ACTION_TYPE.grabOutlineSection) : undefined),
                        onTouchEnd: !interactionDisabled ? onDragIndicatorMouseUp : undefined,
                        onMouseUp: !interactionDisabled ? onDragIndicatorMouseUp : undefined,
                    })}
                    {...getPressEventHandlers({
                        onTouchStart: !interactionDisabled ? onDragIndicatorMouseDown : undefined,
                        onMouseDown: !interactionDisabled ? onDragIndicatorMouseDown : undefined,
                    })}
                >
                    <ReactSVG
                        src={DragIndicatorIcon}
                    />
                </OutlineSectionDragIndicator>
            )}
            <SectionNonEmptyIndicator
                isVisible={nonEmptyIndicatorVisible}
                transitionDuration={CHAPTER_INDICATOR_TRANSITION_DURATION}
            >
                <Tooltip
                    text="Section has content"
                    side={TOOLTIP_TYPE.left}
                />
                <ReactSVG
                    src={PencilIcon}
                />
            </SectionNonEmptyIndicator>
            <SectionAnnotationsIndicator
                isVisible={annotationCount > 0}
                transitionDuration={CHAPTER_INDICATOR_TRANSITION_DURATION}
            >
                <Tooltip
                    text="Section has annotations"
                    side={TOOLTIP_TYPE.left}
                />
                <ReactSVG
                    src={HighlightIcon}
                />
            </SectionAnnotationsIndicator>
            {isSelected && (
                <SectionDisabledIndicator
                    isVisible={interactionDisabled}
                    annotationsIndicatorIsVisible={annotationCount > 0}
                    transitionDuration={CHAPTER_INDICATOR_TRANSITION_DURATION}
                >
                    <Tooltip
                        text="Post is being updated"
                        side={TOOLTIP_TYPE.left}
                    />
                    <ReactSVG
                        src={DisabledIcon}
                    />
                </SectionDisabledIndicator>
            )}
        </Container>
    );
}

export default ReaderOutlineSection;
